import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { StringUtility } from '../utility/string.utility';
import { IsNotMatchValidator } from '../validators/is-not-match.validator';
import { BaseResponse } from './base-response.model';
import { UserDropDownJSON } from './user-drop-down.model';

export interface ApproverAssignmentsResponse extends BaseResponse {
	message: string;
	dropdowns: UserDropDownJSON[];
	approverAssignments: ApproverAssignmentJSON[];
}

export interface ApproverAssignmentJSON {
	userId: number;
	userLastName: string;
	userFirstName: string;
	appointmentId: number;
	departmentName: string;
	jobTitle: string;
	supervisorId: number;
	supervisorNetId: string;
	supervisorLastName: string;
	supervisorFirstName: string;
	tarApproverId: number;
	tarApproverNetId: string;
	tarApproverLastName: string;
	tarApproverFirstName: string;
}

export interface ApproverAssignmentsFormGroup {
	assignments: FormArray<FormGroup<ApproverAssignmentFormGroup>>;
}

export interface ApproverAssignmentFormGroup {
	assignment: FormControl<ApproverAssignment>;
	supervisorId: FormControl<number>;
	tarApproverId: FormControl<number>;
}

export class ApproverAssignment {
	constructor(
		public userId: number,
		public userLastName: string,
		public userFirstName: string,
		public appointmentId: number,
		public departmentName: string,
		public jobTitle: string,
		public supervisorId: number,
		public supervisorNetId: string,
		public supervisorLastName: string,
		public supervisorFirstName: string,
		public tarApproverId: number,
		public tarApproverNetId: string,
		public tarApproverLastName: string,
		public tarApproverFirstName: string
	) {}

	get userName(): string {
		return StringUtility.lastFirstName(
			this.userLastName,
			this.userFirstName
		);
	}

	get supervisorName(): string {
		return StringUtility.lastFirstName(
			this.supervisorLastName,
			this.supervisorFirstName,
			this.supervisorNetId
		);
	}

	get tarApproverName(): string {
		return StringUtility.lastFirstName(
			this.tarApproverLastName,
			this.tarApproverFirstName,
			this.tarApproverNetId
		);
	}

	static fromJSON(json: ApproverAssignmentJSON): ApproverAssignment {
		return Object.assign(
			Object.create(ApproverAssignment.prototype),
			json,
			{}
		);
	}

	static createAssignmentsFormGroup(
		assignments: ApproverAssignment[],
		managerIds: number[],
		userId: number
	): FormGroup<ApproverAssignmentsFormGroup> {
		return new FormGroup<ApproverAssignmentsFormGroup>({
			assignments: this.createAssignmentsFormArray(
				assignments,
				managerIds,
				userId
			),
		});
	}

	static createAssignmentsFormArray(
		assignments: ApproverAssignment[],
		managerIds: number[],
		userId: number
	): FormArray<FormGroup<ApproverAssignmentFormGroup>> {
		return assignments
			? new FormArray<FormGroup<ApproverAssignmentFormGroup>>(
					assignments.map((assignment) =>
						this.createAssignmentFormGroup(
							assignment,
							managerIds,
							userId
						)
					)
				)
			: new FormArray([]);
	}

	static createAssignmentFormGroup(
		assignment: ApproverAssignment,
		managerIds: number[],
		userId: number
	): FormGroup<ApproverAssignmentFormGroup> {
		const fg: FormGroup<ApproverAssignmentFormGroup> =
			new FormGroup<ApproverAssignmentFormGroup>({
				assignment: new FormControl(assignment),
				supervisorId: new FormControl(
					managerIds.includes(assignment.supervisorId)
						? assignment.supervisorId
						: null,
					[Validators.required, IsNotMatchValidator(userId)]
				),
				tarApproverId: new FormControl(
					managerIds.includes(assignment.tarApproverId)
						? assignment.tarApproverId
						: null,
					[Validators.required, IsNotMatchValidator(userId)]
				),
			});
		fg.markAllAsTouched();
		return fg;
	}

	static fromAssignmentsFormGroup(
		fg: FormGroup<ApproverAssignmentsFormGroup>
	): UpdatedApproverAssignment[] {
		return this.fromAssignmentsFormArray(
			fg.get('assignments') as FormArray<
				FormGroup<ApproverAssignmentFormGroup>
			>
		);
	}

	static fromAssignmentsFormArray(
		fa: FormArray<FormGroup<ApproverAssignmentFormGroup>>
	): UpdatedApproverAssignment[] {
		return fa.length > 0
			? fa.controls.map((fg) => this.fromAssignmentFormGroup(fg))
			: [];
	}

	static fromAssignmentFormGroup(
		fg: FormGroup<ApproverAssignmentFormGroup>
	): UpdatedApproverAssignment {
		return {
			userId: fg.get('assignment').value.userId,
			appointmentId: fg.get('assignment').value.appointmentId,
			supervisorId: fg.get('supervisorId').value,
			tarApproverId: fg.get('tarApproverId').value,
		};
	}
}

export interface UpdatedApproverAssignment {
	userId: number;
	appointmentId: number;
	supervisorId: number;
	tarApproverId: number;
}
